import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PencilSquareIcon } from "@heroicons/react/20/solid";
import { EMPTY_TAG_VALUE_LABEL, formatTagValue } from "./formatTagValue";
import { useTagAliases } from "./context";

interface TagValueProps {
  /** The raw tag value — the identity. Never changed by anything here. */
  value: string;
  /** Classes for the label text (width, truncation, type scale). */
  className?: string;
  /**
   * Offer the rename affordance. Default true; set false where a row is too
   * dense to carry a control (the traces table shows several tags per cell).
   */
  editable?: boolean;
}

/**
 * A tag value as a human reads it, renameable where it is read (issue #1410).
 *
 * The single place display and rename meet: wherever a customer sees a UUID
 * they can give it a name, and that name then renders everywhere else the value
 * appears. The raw value stays the identity throughout — this component only
 * ever changes text.
 */
export function TagValue({ value, className = "", editable = true }: TagValueProps) {
  const { aliases, canRename } = useTagAliases();
  const [editing, setEditing] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const display = formatTagValue(value, aliases);
  const raw = value === "" ? EMPTY_TAG_VALUE_LABEL : value;
  const showEdit = editable && canRename;

  return (
    <span
      className="inline-flex items-center gap-1 min-w-0 group/tag-value"
      data-testid="tag-value"
      data-tag-value={value}
    >
      <span
        className={`min-w-0 ${className}`}
        title={display === raw ? raw : `${display} — ${raw}`}
      >
        {display}
      </span>
      {showEdit && (
        <button
          ref={triggerRef}
          type="button"
          // Visible on hover/focus so a dense chart isn't a field of pencils,
          // but always in the tab order so it is reachable without a mouse.
          className="shrink-0 opacity-0 group-hover/tag-value:opacity-100 focus:opacity-100 text-muted-foreground hover:text-foreground transition-opacity"
          // Labelled with what is on screen, which is what a screen-reader user
          // just heard — the raw value is in the editor once it opens.
          aria-label={`Rename ${display}`}
          title={`Rename ${display}`}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
        >
          <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
      {editing && (
        <TagAliasEditor
          value={value}
          anchor={triggerRef.current}
          onClose={() => setEditing(false)}
        />
      )}
    </span>
  );
}

interface TagAliasEditorProps {
  value: string;
  anchor: HTMLElement | null;
  onClose: () => void;
}

/**
 * The rename popover.
 *
 * Rendered into `document.body` and positioned against the trigger's box, not
 * nested where it is triggered from. The two places that need renaming most sit
 * in hostile containers: the heatmap header and the filter dropdown are both
 * inside scrollers that would clip it, and the filter dropdown's rows are
 * `<label>`s, where a click on a nested non-interactive element toggles the
 * checkbox. A portal is immune to both. It closes on scroll, since a box
 * positioned once doesn't follow its anchor.
 */
function TagAliasEditor({ value, anchor, onClose }: TagAliasEditorProps) {
  const { aliases, rename, clearName } = useTagAliases();
  const currentName = aliases[value];
  const [draft, setDraft] = useState(currentName ?? "");
  const [saving, setSaving] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const rect = anchor?.getBoundingClientRect();

  const save = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    // Clearing the field means "stop naming this" — the hook turns that into a
    // delete, so there is one control, not a separate Remove button.
    await rename(value, draft);
    setSaving(false);
    onClose();
  }, [draft, onClose, rename, saving, value]);

  const remove = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    await clearName(value);
    setSaving(false);
    onClose();
  }, [clearName, onClose, saving, value]);

  // Dismiss on an outside click or a scroll; Escape is handled on the input so
  // it fires while the caret is in the field.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", onClose, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  const buttonClasses = "px-2 py-1 rounded text-xs font-medium";

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Rename tag value"
      data-testid="tag-alias-editor"
      className="fixed z-50 w-64 bg-card border border-border rounded-lg shadow-lg p-3 text-left"
      style={{ top: (rect?.bottom ?? 0) + 6, left: rect?.left ?? 0 }}
      // Stops the click reaching a row that would treat it as a selection.
      onClick={(e) => e.stopPropagation()}
    >
      <label
        className="block text-xs font-medium text-muted-foreground mb-1"
        htmlFor="tag-alias-input"
      >
        Display name
      </label>
      <input
        id="tag-alias-input"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            save();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          }
        }}
        placeholder="e.g. Deckers"
        className="w-full px-2 py-1 text-sm bg-background border border-border rounded focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {/* The raw value stays on screen while renaming: it is what the customer's
          own systems use, and the name is only ever a label over it. */}
      <p
        className="mt-1.5 text-[11px] font-mono text-muted-foreground truncate"
        title={value}
      >
        {value === "" ? EMPTY_TAG_VALUE_LABEL : value}
      </p>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className={`${buttonClasses} bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50`}
        >
          Save
        </button>
        {currentName !== undefined && (
          <button
            type="button"
            onClick={remove}
            disabled={saving}
            className={`${buttonClasses} border border-border text-muted-foreground hover:bg-accent disabled:opacity-50`}
          >
            Remove
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className={`${buttonClasses} text-muted-foreground hover:bg-accent ml-auto`}
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );
}
